import dayjs, { type Dayjs } from "dayjs";
import type {
  SchedulePublicAvailableSlot,
  SchedulePublicBookingDetails,
  SchedulePublicBookingLink,
  SchedulePublicHost,
  SchedulePublicLocation,
} from "@/api/scheduling/schedule-public.types";
import { VIDEO_PROVIDER_LOGOS } from "@/assets";
import { colors } from "@/design-system/theme/colors";

const HEX6_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Resolve the workspace brand color, falling back to the design-system primary. */
export const getBrandColor = (
  bookingLink: SchedulePublicBookingLink | undefined,
): string => bookingLink?.workspace?.brandColor?.trim() || colors.primary;

/**
 * Append an alpha channel to a 6-digit hex color (e.g. "#003D8A" → "#003D8A24").
 * Non-hex colors are returned unchanged so callers should pair this with a tint
 * fallback when a translucent fill is required.
 */
export const withBrandAlpha = (color: string, alpha: number): string => {
  if (!HEX6_COLOR_PATTERN.test(color)) return color;
  const clamped = Math.min(1, Math.max(0, alpha));
  const hexAlpha = Math.round(clamped * 255)
    .toString(16)
    .padStart(2, "0");
  return `${color}${hexAlpha}`;
};

/**
 * A light tint of the brand color for subtle fills (selected slots, host
 * avatars, etc.). Uses a low-alpha hex when the brand color is a valid 6-digit
 * hex, otherwise falls back to the design-system primary tint.
 */
export const getBrandTint = (color: string, alpha = 0.14): string =>
  HEX6_COLOR_PATTERN.test(color)
    ? withBrandAlpha(color, alpha)
    : colors.primaryExtraLight;

export type PublicBookingTimeFormat = "12h" | "24h";

const DATE_FORMAT = "YYYY-MM-DD";

export const normalizePublicBookingEmail = (email: string): string =>
  email.trim().toLowerCase();

export const isValidPublicBookingEmail = (email: string): boolean =>
  EMAIL_PATTERN.test(email.trim());

const isPolicyDisabledForAttendee = (
  disabledFor: string | null | undefined,
): boolean => {
  const value = disabledFor?.trim().toUpperCase();
  return value === "ATTENDEE_ONLY" || value === "HOST_AND_ATTENDEE";
};

const getBookingStartDate = (
  booking: SchedulePublicBookingDetails | null | undefined,
): Dayjs | null => {
  const startTime =
    booking?.inviteeStartTime?.trim() || booking?.startTime?.trim() || "";
  if (!startTime) return null;
  const date = dayjs(startTime);
  return date.isValid() ? date : null;
};

export const canReschedulePublicBooking = (
  bookingLink: SchedulePublicBookingLink | undefined,
  booking?: SchedulePublicBookingDetails | null,
): boolean => {
  if (!bookingLink || bookingLink.allowReschedule !== true) return false;

  const policy = bookingLink.reschedulePolicy;
  const policyEnabled = policy?.enabled === true;
  const disabledFor = policyEnabled
    ? policy?.disabledFor
    : bookingLink.rescheduleDisabledFor;
  const disableRule = (
    policyEnabled ? policy?.disableRule : bookingLink.rescheduleDisableRule
  )?.trim().toUpperCase();
  const cutoffMinutes = Math.max(
    0,
    policyEnabled
      ? policy?.cutoffMinutes ?? 0
      : bookingLink.rescheduleCutoffMinutes ?? 0,
  );

  if (isPolicyDisabledForAttendee(disabledFor)) {
    if (!disableRule || disableRule === "ALWAYS") return false;

    if (disableRule === "BEFORE_EVENT_START") {
      const startDate = getBookingStartDate(booking);
      if (!startDate) return true;
      const now = dayjs();
      if (now.isBefore(startDate)) {
        return now.isBefore(startDate.subtract(cutoffMinutes, "minute"));
      }
    }
  }

  const allowPastEvents =
    policyEnabled && typeof policy?.allowPastEvents !== "undefined"
      ? policy.allowPastEvents === true
      : bookingLink.allowReschedulingPastEvents === true;

  if (!allowPastEvents) {
    const startDate = getBookingStartDate(booking);
    if (startDate && !dayjs().isBefore(startDate)) return false;
  }

  return true;
};

export const getPublicBookingGuestEmailErrors = ({
  guestEmails,
  inviteeEmail,
  maxInvitees,
}: {
  guestEmails: readonly string[];
  inviteeEmail: string;
  maxInvitees: number;
}): Record<number, string> => {
  const errors: Record<number, string> = {};
  const normalizedInviteeEmail = normalizePublicBookingEmail(inviteeEmail);
  const normalizedGuestEmailCounts = guestEmails.reduce<Record<string, number>>(
    (counts, email) => {
      const normalizedEmail = normalizePublicBookingEmail(email);
      if (normalizedEmail) {
        counts[normalizedEmail] = (counts[normalizedEmail] ?? 0) + 1;
      }
      return counts;
    },
    {},
  );

  guestEmails.forEach((email, index) => {
    const trimmedEmail = email.trim();
    const normalizedEmail = normalizePublicBookingEmail(email);
    if (!trimmedEmail) return;

    if (!isValidPublicBookingEmail(trimmedEmail)) {
      errors[index] = "Enter a valid guest email address.";
      return;
    }

    if (normalizedEmail === normalizedInviteeEmail) {
      errors[index] = "Guest email must be different from invitee email.";
      return;
    }

    if (normalizedGuestEmailCounts[normalizedEmail] > 1) {
      errors[index] = "This guest email is already added.";
    }
  });

  const guestCount = guestEmails.filter((email) => email.trim()).length;
  if (guestCount + 1 > maxInvitees) {
    errors[Math.max(0, maxInvitees - 1)] =
      `This booking allows up to ${maxInvitees} invitees.`;
  }

  return errors;
};

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/**
 * Whether the invitee may pick their own timezone. Mirrors the condition in
 * {@link getBookingTimezone}: selection is only offered when the booking link
 * explicitly allows it and the timezone is not locked to the event/host.
 */
export const canSelectBookingTimezone = (
  bookingLink: SchedulePublicBookingLink | undefined,
): boolean =>
  Boolean(bookingLink?.settings?.allowInviteeTimezoneSelection) &&
  !bookingLink?.lockTimezone;

export const getBookingTimezone = (
  bookingLink: SchedulePublicBookingLink | undefined,
): string => {
  if (!bookingLink) return getBrowserTimezone();

  if (canSelectBookingTimezone(bookingLink)) {
    return getBrowserTimezone();
  }

  return bookingLink.timezone || bookingLink.workspace.timezone || getBrowserTimezone();
};

export const formatDateParam = (date: Dayjs): string => date.format(DATE_FORMAT);

export interface AvailabilityDateRange {
  from: string;
  to: string;
}

/**
 * The backend rejects availability requests whose span exceeds 62 days. We
 * keep each chunk just under that limit and stitch several chunks together to
 * cover roughly six months of availability.
 */
export const AVAILABILITY_MAX_RANGE_DAYS = 62;
/** (to - from) per chunk, kept at/under the backend's 62-day hard limit. */
export const AVAILABILITY_CHUNK_SPAN_DAYS = 61;
/** Number of contiguous chunks fetched up front (~6 months of coverage). */
export const AVAILABILITY_CHUNK_COUNT = 3;

const FIXED_BOOKING_WINDOW_TYPES = new Set(["fixed", "fixed_range"]);

export const isFixedPublicBookingWindow = (
  bookingWindowType: string | null | undefined,
): boolean => FIXED_BOOKING_WINDOW_TYPES.has(bookingWindowType ?? "");

const parseBookingWindowDate = (date: string | null | undefined): Dayjs | null => {
  if (!date) return null;
  const parsedDate = dayjs(date).startOf("day");
  return parsedDate.isValid() ? parsedDate : null;
};

export const getPublicBookingWindowStartDate = (
  bookingLink: SchedulePublicBookingLink | undefined,
): Dayjs | null => {
  if (!isFixedPublicBookingWindow(bookingLink?.bookingWindowType)) return null;
  return parseBookingWindowDate(bookingLink?.bookingWindowStartDate);
};

export const getPublicBookingWindowEndDate = (
  bookingLink: SchedulePublicBookingLink | undefined,
): Dayjs | null => {
  if (!isFixedPublicBookingWindow(bookingLink?.bookingWindowType)) return null;
  return parseBookingWindowDate(bookingLink?.bookingWindowEndDate);
};

const getMinimumBookingDate = (
  bookingLink: SchedulePublicBookingLink | undefined,
): Dayjs => {
  const today = dayjs().startOf("day");
  const startDate = getPublicBookingWindowStartDate(bookingLink);
  return startDate && startDate.isAfter(today, "day") ? startDate : today;
};

/**
 * Builds the contiguous list of `from`/`to` ranges (inclusive, YYYY-MM-DD) for
 * the available-dates requests. Each range stays within the backend's 62-day
 * limit, the set spans ~6 months, and it never requests past dates. Fixed
 * booking windows anchor at their configured start date, then shift forward to
 * keep covering `visibleMonth` if the visitor navigates beyond the prefetched
 * range.
 */
export const getAvailabilityDateRanges = (
  visibleMonth: Dayjs,
  bookingLink?: SchedulePublicBookingLink,
  chunkCount: number = AVAILABILITY_CHUNK_COUNT,
  chunkSpanDays: number = AVAILABILITY_CHUNK_SPAN_DAYS,
): AvailabilityDateRange[] => {
  const stride = Math.min(chunkSpanDays, AVAILABILITY_MAX_RANGE_DAYS - 1) + 1;
  const windowSpanDays = chunkCount * stride;
  const visibleStart = visibleMonth.startOf("month").startOf("day");
  const minimumBookingDate = getMinimumBookingDate(bookingLink);

  const anchor = visibleStart.isAfter(minimumBookingDate.add(windowSpanDays, "day"))
    ? visibleStart
    : minimumBookingDate;

  return Array.from({ length: Math.max(1, chunkCount) }, (_, index) => {
    const from = anchor.add(index * stride, "day");
    const to = from.add(stride - 1, "day");
    return { from: formatDateParam(from), to: formatDateParam(to) };
  });
};

export const getInitialBookingDate = (
  bookingLink: SchedulePublicBookingLink | undefined,
): Dayjs => {
  return getMinimumBookingDate(bookingLink);
};

export const isDateInPublicBookingWindow = (
  date: Dayjs,
  bookingLink: SchedulePublicBookingLink | undefined,
): boolean => {
  const day = date.startOf("day");
  if (day.isBefore(dayjs().startOf("day"), "day")) return false;

  const startDate = getPublicBookingWindowStartDate(bookingLink);
  if (startDate && day.isBefore(startDate, "day")) return false;

  const endDate = getPublicBookingWindowEndDate(bookingLink);
  if (endDate && day.isAfter(endDate, "day")) return false;

  return true;
};

/**
 * Resolves whether a date can be booked. Once the backend availability set has
 * loaded, that response is the source of truth for selectable dates.
 */
export const isDateAvailable = (
  date: Dayjs,
  availableDates: ReadonlySet<string> | null,
  bookingLink?: SchedulePublicBookingLink,
): boolean => {
  if (!isDateInPublicBookingWindow(date, bookingLink)) return false;
  if (!availableDates) return true;
  return availableDates.has(formatDateParam(date));
};

/** Returns the earliest backend-confirmed available date, or null when none. */
export const getFirstAvailableDate = (
  availableDates: ReadonlySet<string> | null,
): Dayjs | null => {
  if (!availableDates || availableDates.size === 0) return null;
  let earliest: Dayjs | null = null;
  availableDates.forEach((dateKey) => {
    const date = dayjs(dateKey).startOf("day");
    if (!date.isValid()) return;
    if (!earliest || date.isBefore(earliest)) earliest = date;
  });
  return earliest;
};

export const getDefaultLocation = (
  locations: SchedulePublicLocation[] | undefined,
): SchedulePublicLocation | undefined => {
  if (!locations?.length) return undefined;
  return locations.find((location) => location.isDefault) ?? locations[0];
};

export const formatLocationLabel = (
  location: SchedulePublicLocation | undefined,
): string => {
  if (!location) return "Online meeting";

  const explicitValue = location.locationValue?.trim();
  if (explicitValue) return explicitValue;

  const locationLabels: Record<string, string> = {
    google_meet: "Google Meet",
    zoom: "Zoom",
    microsoft_teams: "Microsoft Teams",
    phone: "Phone call",
    in_person: "In person",
    custom: "Custom location",
  };

  return locationLabels[location.locationType] ?? "Online meeting";
};

/**
 * Returns the hosted brand logo URL for a video-conferencing location type
 * (google_meet | zoom | microsoft_teams), or null for non-video locations
 * (custom, phone, in_person, …) so callers can fall back to a generic icon.
 */
export const getLocationProviderLogo = (
  locationType: string | undefined | null,
): string | null => {
  if (!locationType) return null;
  return VIDEO_PROVIDER_LOGOS[locationType] ?? null;
};

export const getSlotTimeParts = (
  value: string,
): { hours: number; minutes: number } | null => {
  const match = value.match(/T(\d{2}):(\d{2})/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return { hours, minutes };
};

export const formatSlotTime = (
  value: string,
  format: PublicBookingTimeFormat,
): string => {
  const parts = getSlotTimeParts(value);
  if (!parts) return value;

  if (format === "24h") {
    return `${String(parts.hours).padStart(2, "0")}:${String(parts.minutes).padStart(2, "0")}`;
  }

  const suffix = parts.hours >= 12 ? "pm" : "am";
  const hour = parts.hours % 12 || 12;
  return `${hour}:${String(parts.minutes).padStart(2, "0")}${suffix}`;
};

export const getVisibleSlots = (
  slots: SchedulePublicAvailableSlot[] | undefined,
): SchedulePublicAvailableSlot[] => {
  if (!slots?.length) return [];
  return slots.filter((slot) => slot.available);
};

export const getHostName = (host: SchedulePublicHost | undefined): string => {
  if (!host) return "";
  const fullName = [host.userFirstName, host.userLastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return host.userName?.trim() || fullName || host.userEmail?.trim() || "";
};

/**
 * Display name(s) of the actual host(s) for this booking link. Never falls back
 * to the workspace name — only real host identities are shown.
 */
export const getHostDisplayName = (
  bookingLink: SchedulePublicBookingLink | undefined,
): string => {
  if (!bookingLink) return "";
  return (bookingLink.hosts || [])
    .map(getHostName)
    .filter(Boolean)
    .join(", ");
};
